// What Livechat needs from the app that hosts it (#216), handed over once at
// bootstrap — the same pattern as core's registerHttpClients and
// datacentral-react's configureDataHooks. The host calls createLivechatModule
// from src/app/register-livechat.tsx; nothing here imports the app.
//
// Livechat opens no registry modals (its sync drawer renders in place) and does
// not read the admin flag, so it does not extend the shell's HostModalApi /
// HostSession. It reads the signed-in employee and the access token the chat
// socket authenticates with. Every member is typed structurally, so the host's
// real objects are checked where it registers them, not here.
import type { ComponentType } from 'react'
import type { Control } from 'react-hook-form'
import type { HostLinks } from '@vnetwork/datacentral-shell/host'
import vi from './locales/vi.json'
import en from './locales/en.json'

/** The signed-in employee, as far as Livechat reads it. */
export interface LivechatEmployee {
    employee_id: number
    employee_email_company: string
    employee_uuid: string
}

/** A `YYYY-MM-DD` range, as the analytics page's date selector emits it. */
export interface LivechatDateRange {
    gte: string
    lte: string
}

/** The props Livechat gives the host's date range selector. */
export interface LivechatDateRangeSelectorProps {
    value?: LivechatDateRange
    onChange: (range: LivechatDateRange) => void
    placement?: 'left' | 'right'
}

/** The props Livechat gives the host's employee select (sync drawer). */
export interface LivechatEmployeeSelectProps {
    control: Control<any>
    name: string
    multiple?: boolean
    onlyActive?: boolean
    label?: string
    departmentIds?: number[]
}

/** The props Livechat gives the host's product-group select (sync drawer). */
export interface LivechatProductGroupSelectProps {
    control: Control<any>
    name: string
    multiple?: boolean
    showLabel?: boolean
    label?: string
    placeholder?: string
}

export interface LivechatHost {
    /**
     * The app's i18next instance, already initialised. The `livechat`
     * namespace is registered against it here. A package must not import
     * i18next itself to add bundles: that could hit an instance before the
     * app's init (#59 hazard 5).
     */
    i18n: {
        addResourceBundle: (
            lng: string,
            ns: string,
            resources: object
        ) => unknown
    }
    /**
     * Pages of OTHER modules Livechat links to. `customerMkt` is the Marketing
     * list the sync drawer and the room info lead to (it was `paths.customerMkt`
     * of the app-wide `paths` object); the package appends its own
     * `?contact_source_id=` query.
     */
    links: HostLinks<'customerMkt'>
    /** The signed-in employee, read at render time. */
    useCurrentEmployee: () => { employee: LivechatEmployee | null }
    /**
     * The access token the chat socket sends as `auth.authorization` (the one
     * the app uses for API calls), read when the socket connects.
     */
    getAccessToken: () => string | null | undefined
    /** The app's date range selector (analytics page header). */
    DateRangeSelector: ComponentType<LivechatDateRangeSelectorProps>
    /** The app's employee select (assignees of the sync drawer). */
    EmployeeSelect: ComponentType<LivechatEmployeeSelectProps>
    /** The app's product-group select (interested services of the sync drawer). */
    ProductGroupSelect: ComponentType<LivechatProductGroupSelectProps>
}

let current: LivechatHost | undefined

export function registerLivechatHost(host: LivechatHost): void {
    current = host
    host.i18n.addResourceBundle('vi', 'livechat', vi)
    host.i18n.addResourceBundle('en', 'livechat', en)
}

/** The registered host. Read it at render or call time, never at import. */
export function getLivechatHost(): LivechatHost {
    if (!current)
        throw new Error(
            '[livechat] no host registered. Call createLivechatModule({ i18n, links, … }) at app bootstrap (src/app/register-livechat.tsx).'
        )
    return current
}

// The host's session hook, under the name the app's own has, so call sites read
// as before. It reads the host at call time, inside a component.
export const useCurrentEmployee = () => getLivechatHost().useCurrentEmployee()
